import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getPublicVehicle, getPublicVehicleStats } from '../../lib/supabase'
import { getScoreTier, calculateVehicleScore } from '../../lib/garageScore'
import {
  AlertCircle,
  Eye,
  Droplets,
  Zap,
  Calendar,
  Users,
  FileText,
  Wrench,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Shield,
  CheckCircle,
  Clock,
  Share2,
  Copy,
  Check
} from 'lucide-react'
import { format } from 'date-fns'

// Extract first name + last initial from full name
const formatOwnerName = (fullName) => {
  if (!fullName) return 'Owner'
  const parts = fullName.trim().split(' ')
  const firstName = parts[0]
  const lastName = parts[parts.length - 1]
  return `${firstName} ${lastName.charAt(0)}.`
}

// Private vehicle message
const PrivateVehicleMessage = () => (
  <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-4">
    <div className="card max-w-md w-full p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-rdc-tan/20 flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={24} className="text-rdc-tan" />
      </div>
      <h1 className="text-2xl font-display font-semibold text-black mb-2">This Vehicle is Private</h1>
      <p className="text-rdc-taupe mb-6">The owner hasn't shared this vehicle publicly yet.</p>
      <Link to="/" className="btn-primary inline-block">Back to Home</Link>
    </div>
  </div>
)

// Loading state
const LoadingSpinner = () => (
  <div className="min-h-screen bg-rdc-cream flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-rdc-primary border-t-transparent mx-auto mb-4"></div>
      <p className="text-rdc-taupe">Loading vehicle provenance...</p>
    </div>
  </div>
)

// Photo gallery with navigation
const PhotoGallery = ({ images, primaryImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const allImages = []
  if (primaryImage) allImages.push({ url: primaryImage, isPrimary: true })
  if (images && images.length > 0) {
    images.forEach(img => {
      if (img.url !== primaryImage) allImages.push({ url: img.url })
    })
  }

  if (allImages.length === 0) return null

  const handlePrev = () => setCurrentIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)
  const handleNext = () => setCurrentIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1)

  return (
    <div className="relative w-full bg-black/5">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <img
          src={allImages[currentIndex].url}
          alt={`Vehicle photo ${currentIndex + 1}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>

        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all z-10"
            >
              <ChevronLeft size={24} className="text-black" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full transition-all z-10"
            >
              <ChevronRight size={24} className="text-black" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-xs font-medium">
              {currentIndex + 1} / {allImages.length}
            </div>
          </>
        )}
      </div>

      {allImages.length > 1 && (
        <div className="hidden sm:flex gap-2 p-4 bg-white overflow-x-auto">
          {allImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                idx === currentIndex
                  ? 'ring-2 ring-rdc-primary scale-105'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img.url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Provenance verification badge
const ProvenanceBadge = ({ serviceCount, documentCount, valuationCount }) => {
  const totalDocs = serviceCount + documentCount + valuationCount
  let level = 'Basic'
  let color = 'text-gray-600 bg-gray-50 border-gray-200'
  let icon = FileText

  if (totalDocs >= 15) {
    level = 'Comprehensive'
    color = 'text-green-700 bg-green-50 border-green-200'
    icon = Shield
  } else if (totalDocs >= 8) {
    level = 'Well Documented'
    color = 'text-blue-700 bg-blue-50 border-blue-200'
    icon = CheckCircle
  } else if (totalDocs >= 3) {
    level = 'Documented'
    color = 'text-amber-700 bg-amber-50 border-amber-200'
    icon = FileText
  }

  const Icon = icon

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium ${color}`}>
      <Icon size={16} />
      <span>{level} Provenance</span>
    </div>
  )
}

// Timeline item for provenance
const TimelineItem = ({ icon: Icon, label, value, sublabel }) => (
  <div className="flex items-start gap-4 py-4">
    <div className="w-10 h-10 rounded-full bg-rdc-cream flex items-center justify-center flex-shrink-0">
      <Icon size={18} className="text-rdc-primary" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-black">{label}</p>
      <p className="text-lg font-semibold text-rdc-primary">{value}</p>
      {sublabel && <p className="text-xs text-rdc-taupe mt-0.5">{sublabel}</p>}
    </div>
  </div>
)

export default function VehicleShowcase() {
  const { vehicleId } = useParams()
  const [vehicle, setVehicle] = useState(null)
  const [stats, setStats] = useState(null)
  const [score, setScore] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    loadData()
  }, [vehicleId])

  const loadData = async () => {
    try {
      setLoading(true)
      setError('')

      const vehicleRes = await getPublicVehicle(vehicleId)
      if (vehicleRes.error || !vehicleRes.data) {
        setError('not_found')
        setLoading(false)
        return
      }

      const vehicleData = vehicleRes.data
      setVehicle(vehicleData)

      const statsRes = await getPublicVehicleStats(vehicleId)
      // getPublicVehicleStats returns {serviceCount, documentCount, ...} directly
      const statsData = statsRes || { serviceCount: 0, documentCount: 0, valuationCount: 0, valuationRange: null }
      setStats(statsData)

      const scoreData = calculateVehicleScore(
        vehicleData,
        statsData.serviceCount,
        statsData.documentCount,
        0, 0
      )
      setScore(scoreData)
    } catch (err) {
      console.error('Error loading vehicle:', err)
      setError('error')
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) return <LoadingSpinner />
  if (error === 'not_found') return <PrivateVehicleMessage />

  if (error || !vehicle || !stats) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8 text-center">
          <AlertCircle size={24} className="text-red-600 mx-auto mb-4" />
          <h1 className="text-xl font-display font-semibold text-black mb-2">Something went wrong</h1>
          <p className="text-rdc-taupe mb-6">We couldn't load this vehicle. Please try again.</p>
          <Link to="/" className="btn-primary inline-block">Back to Home</Link>
        </div>
      </div>
    )
  }

  const ownerName = formatOwnerName(vehicle.owner?.full_name)
  const memberSince = vehicle.owner?.member_since
    ? format(new Date(vehicle.owner.member_since), 'MMMM yyyy')
    : vehicle.owner?.created_at
    ? format(new Date(vehicle.owner.created_at), 'MMMM yyyy')
    : null

  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}`
  const hasCondition = vehicle.condition
  const hasMileage = vehicle.mileage !== null && vehicle.mileage !== undefined

  // Format valuation
  let valuationText = null
  if (stats.valuationRange) {
    const { min, max } = stats.valuationRange
    if (min === max) {
      valuationText = `$${(min / 1000).toFixed(0)}K`
    } else {
      valuationText = `$${(min / 1000).toFixed(0)}K - $${(max / 1000).toFixed(0)}K`
    }
  }

  // Build powertrain string
  const powertrainParts = []
  if (vehicle.fuel_type) powertrainParts.push(vehicle.fuel_type)
  if (vehicle.engine_info) powertrainParts.push(vehicle.engine_info)
  if (vehicle.drive_type) powertrainParts.push(vehicle.drive_type)
  const powertrain = powertrainParts.join(' · ')

  // Condition labels
  const conditionLabels = {
    concours: 'Concours / Show Quality',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    project: 'Project / Restoration'
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-rdc-primary" />
            <span className="text-sm font-semibold text-black">Vehicle Provenance Report</span>
          </div>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rdc-taupe hover:text-black bg-gray-50 hover:bg-gray-100 rounded-full transition-colors"
          >
            {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Share Link'}
          </button>
        </div>
      </div>

      {/* Photo Gallery */}
      <PhotoGallery
        images={[]}
        primaryImage={vehicle.primary_image_url}
      />

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12">

        {/* Vehicle Identity */}
        <div className="mb-10">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-black">
                {vehicleTitle}
              </h1>
              {vehicle.trim && (
                <p className="text-lg text-rdc-taupe mt-1">{vehicle.trim}</p>
              )}
            </div>
            {stats && (
              <ProvenanceBadge
                serviceCount={stats.serviceCount}
                documentCount={stats.documentCount}
                valuationCount={stats.valuationCount || 0}
              />
            )}
          </div>

          {/* Quick facts row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-rdc-taupe">
            {vehicle.color && (
              <span>{vehicle.color}</span>
            )}
            {hasMileage && (
              <span>{Number(vehicle.mileage).toLocaleString()} miles</span>
            )}
            {hasCondition && (
              <span>Condition: {conditionLabels[vehicle.condition] || vehicle.condition}</span>
            )}
            {powertrain && (
              <span>{powertrain}</span>
            )}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

          {/* Left column - Provenance timeline */}
          <div className="lg:col-span-2 space-y-8">

            {/* Documentation Summary */}
            <div>
              <h2 className="text-lg font-display font-semibold text-black mb-1">Documentation History</h2>
              <p className="text-sm text-rdc-taupe mb-6">
                This vehicle's records are maintained by {ownerName} through the RDC Digital Garage platform.
              </p>

              <div className="border border-gray-100 rounded-xl divide-y divide-gray-100">
                <TimelineItem
                  icon={Wrench}
                  label="Service Records"
                  value={stats.serviceCount > 0 ? `${stats.serviceCount} documented service${stats.serviceCount !== 1 ? 's' : ''}` : 'No records yet'}
                  sublabel={stats.serviceCount > 0 ? 'Maintenance history tracked and verified by owner' : null}
                />
                <TimelineItem
                  icon={FileText}
                  label="Documents on File"
                  value={stats.documentCount > 0 ? `${stats.documentCount} document${stats.documentCount !== 1 ? 's' : ''}` : 'No documents yet'}
                  sublabel={stats.documentCount > 0 ? 'Titles, registrations, receipts, and other records' : null}
                />
                {valuationText && (
                  <TimelineItem
                    icon={DollarSign}
                    label="Market Valuation"
                    value={valuationText}
                    sublabel="Based on recent comparable sales and market analysis"
                  />
                )}
                <TimelineItem
                  icon={Users}
                  label="Owner"
                  value={ownerName}
                  sublabel={memberSince ? `RDC member since ${memberSince}` : 'RDC Digital Garage member'}
                />
              </div>
            </div>

            {/* What's verified disclaimer */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-black mb-2 flex items-center gap-2">
                <Eye size={16} className="text-rdc-taupe" />
                About This Report
              </h3>
              <p className="text-xs text-rdc-taupe leading-relaxed">
                This provenance report is generated from records maintained by the vehicle owner through the RDC Digital Garage platform.
                Document counts and service records reflect owner-submitted data. For detailed service history, inspection reports,
                or purchase inquiries, contact the owner through the RDC network. Personal contact information, exact locations,
                and financial details are not displayed on public reports.
              </p>
            </div>
          </div>

          {/* Right column - Vehicle details & score */}
          <div className="space-y-6">

            {/* Provenance Score Card */}
            {score && (
              <div className={`rounded-xl p-5 border ${score.tierInfo.borderClass} ${score.tierInfo.bgClass}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-semibold uppercase tracking-wide ${score.tierInfo.textClass}`}>Provenance Score</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${score.tierInfo.textClass} bg-white/50`}>{score.tierInfo.name}</span>
                </div>
                <div className={`text-4xl font-display font-bold ${score.tierInfo.textClass}`}>{score.score}</div>
                <p className="text-xs text-rdc-taupe mt-2">
                  Measures documentation completeness based on service records, photos, documents, and vehicle details.
                </p>
              </div>
            )}

            {/* Vehicle Specifications */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-black mb-4">Specifications</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-rdc-taupe">Year</span>
                  <span className="font-medium text-black">{vehicle.year}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-rdc-taupe">Make</span>
                  <span className="font-medium text-black">{vehicle.make}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-rdc-taupe">Model</span>
                  <span className="font-medium text-black">{vehicle.model}</span>
                </div>
                {vehicle.trim && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Trim</span>
                    <span className="font-medium text-black">{vehicle.trim}</span>
                  </div>
                )}
                {vehicle.color && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Exterior</span>
                    <span className="font-medium text-black">{vehicle.color}</span>
                  </div>
                )}
                {vehicle.interior_color && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Interior</span>
                    <span className="font-medium text-black">{vehicle.interior_color}</span>
                  </div>
                )}
                {hasMileage && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Mileage</span>
                    <span className="font-medium text-black">{Number(vehicle.mileage).toLocaleString()}</span>
                  </div>
                )}
                {hasCondition && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Condition</span>
                    <span className="font-medium text-black">{conditionLabels[vehicle.condition] || vehicle.condition}</span>
                  </div>
                )}
                {vehicle.fuel_type && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Fuel</span>
                    <span className="font-medium text-black">{vehicle.fuel_type}</span>
                  </div>
                )}
                {vehicle.engine_info && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Engine</span>
                    <span className="font-medium text-black">{vehicle.engine_info}</span>
                  </div>
                )}
                {vehicle.drive_type && (
                  <div className="flex justify-between text-sm">
                    <span className="text-rdc-taupe">Drive</span>
                    <span className="font-medium text-black">{vehicle.drive_type}</span>
                  </div>
                )}
              </div>
            </div>

            {/* CTA - Join/Learn More */}
            <div className="bg-rdc-primary/5 border border-rdc-primary/20 rounded-xl p-5 text-center">
              <p className="text-sm font-medium text-black mb-2">Build your vehicle's provenance score</p>
              <p className="text-xs text-rdc-taupe mb-4">
                Track service history, store documents, and get market valuations for your collection.
              </p>
              <a href="/" className="inline-flex items-center gap-2 px-4 py-2 bg-rdc-primary text-white text-sm font-medium rounded-lg hover:bg-rdc-primary/90 transition-colors">
                Join RDC Digital Garage
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-100 py-6 mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-rdc-primary" />
            <span className="text-sm font-semibold text-rdc-primary">RDC Digital Garage</span>
          </div>
          <p className="text-xs text-rdc-taupe">Vehicle provenance powered by the Riding & Driving Club</p>
        </div>
      </div>
    </div>
  )
}
